// frontend/src/hooks/useBlockchainGraph.js

import { useRef, useEffect, useState } from "react";
import * as d3 from "d3"; // D3
import API_URL from "../config"; // 與後端溝通的路徑
import axios from "axios";

/**
 * 此 Hook 專門負責區塊鏈可視化的 D3 邏輯 + 區塊鏈交易查詢/處理。
 * 接收一個 svgRef，並回傳必要的資料與方法（trace, allTransactions 等）。
 */
//全局變數
const chainShortNames = {
  BTC: "btc",
  ETH: "eth",
  TRON: "tron",
  BSC: "bsc",
};
export default function useBlockchainGraph(setHoveredNode) {
  // -----------------------------
  // 1. React 狀態
  // -----------------------------
  const [selectedChainType, setSelectedChainType] = useState("BTC");
  const [fillStyle, setFillStyle] = useState(0);
  const [currentAddress, setCurrentAddress] = useState("");
  const [allTransactions, setAllTransactions] = useState([]);
  const [hoveredTransactionNode, setHoveredTransactionNode] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [isFetchingBalance, setIsFetchingBalance] = useState(false);


  // -----------------------------
  // 2. D3 所需參考 (Ref)
  // -----------------------------
  const svgRef = useRef(null);              // 讓外部也能存 SVG 容器
  const linkGroupRef = useRef(null);        // D3 <g> for links
  const nodeGroupRef = useRef(null);        // D3 <g> for nodes
  const simulationRef = useRef(null);       // D3 forceSimulation
  
  // -----------------------------
  // 3. 資料結構 (節點、連線、區塊鏈等)
  // -----------------------------
  const nodesRef = useRef([]);             // nodes
  const linksRef = useRef([]);             // links

  const estimatedAddresesRef = useRef(new Map());
  const discoveredAddressesRef = useRef(new Map());
  const discoveredLinksRef = useRef(new Set());
  const linkedAddressesRef = useRef(new Map());
  const taintedAddressesRef = useRef(new Map());
  const taintOriginRef = useRef("");
  const taintValueRef = useRef("");

  // 時間範圍（預設）
  const [dateMin, setDateMin] = useState(new Date("2000").getTime() / 1000);
  const [dateMax, setDateMax] = useState(new Date("3000").getTime() / 1000);

  const linksMax = 200; // 最多連線限制

  const fetchBalance = async (address, chainType) => {
    try {
      setIsFetchingBalance(true);
  
      const response = await axios.get(`${API_URL}/api/address-balance`, {
        params: { chainType, address, limit: 1 },
      });
  
      // BTC 部分處理保持不變
      if (chainType.toUpperCase() === "BTC") {
        const finalBalance = response.data?.finalBalance || 0;
        setBalanceData([
          {
            tokenName: "BTC",
            balance: parseFloat(finalBalance).toFixed(8),
          },
        ]);
        return;
      }
  
      // 非 BTC 鏈（例如 ETH、TRON、BSC）
      // 從 API 返回的 paginated 資料中處理每個分頁的 tokenList
      const paginatedBalances = response.data.balances || [];
      const processedBalances = paginatedBalances.map(pageItem => {
        if (pageItem.tokenList && Array.isArray(pageItem.tokenList)) {
          pageItem.tokenList = pageItem.tokenList.map(token => ({
            // 使用 token.symbol 優先，若不存在則用 token.token
            token: token.symbol || token.token,
            holdingAmount: token.holdingAmount,
            // 若 valueUsd 為空且有 priceUsd，則計算總價值；否則使用 valueUsd 或 totalTokenValue
            valueUsd: token.valueUsd ||
                      (token.priceUsd 
                        ? (parseFloat(token.holdingAmount) * parseFloat(token.priceUsd)).toFixed(2)
                        : token.totalTokenValue || "")
          }));
        }
        return pageItem;
      });
  
      setBalanceData(processedBalances);
    } catch (error) {
      console.error("餘額查詢失敗:", error);
      setBalanceData([]);
    } finally {
      setIsFetchingBalance(false);
    }
  };
  // -----------------------------
  // 4. 初始化 D3：useEffect
  // -----------------------------
  useEffect(() => {
    if (!svgRef.current || simulationRef.current) return; // 避免重複初始化

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const content = svg.append("g").attr("class", "zoomable-content");
    linkGroupRef.current = content.append("g").attr("class", "links");
    nodeGroupRef.current = content.append("g").attr("class", "nodes");

    // 添加箭頭標記
    svg.append("defs")
      .append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10") // 定義箭頭的視窗
      .attr("refX", 20) // 箭頭的起點位置
      .attr("refY", 0)
      .attr("markerWidth", 8) // 箭頭的寬度
      .attr("markerHeight", 8) // 箭頭的高度
      .attr("orient", "auto") // 根據連線方向自動旋轉
      .append("path")
      .attr("d", "M0,-5L10,0L0,5") // 定義箭頭形狀
      .attr("fill", "#000000"); // 箭頭顏色

    const zoom = d3.zoom().on("zoom", (event) => {
      content.attr("transform", event.transform);
    });

    svg.call(zoom);
    svg.call(zoom.transform, d3.zoomIdentity.translate(0, 0).scale(1));

    const simulation = d3
      .forceSimulation()
      .force("link", d3.forceLink().id((d) => d.id).distance(300))
      .force("charge", d3.forceManyBody().strength(-100))
      .force("center", d3.forceCenter(1100 / 2, 900 / 2))
      .force("collide", d3.forceCollide(30));

    simulationRef.current = simulation;

    return () => simulation.stop();
  }, []);
  function updateNodeColors() {
    const nodeGroup = nodeGroupRef.current;
    if (!nodeGroup) return;

    nodeGroup
      .selectAll("circle")
      .attr("fill", (node) => {
        const currentAddressLower = currentAddress.toLowerCase();
        if (node.id.toLowerCase() === currentAddressLower) {
          return "rgba(255, 255, 0, 0.85)"; // 高亮顏色：黃色
        }
        if (hoveredTransactionNode && node.id === hoveredTransactionNode) {
          return "rgba(0, 200, 0, 0.85)"; // 高亮顏色：綠色
        }
        return "rgba(255, 80, 0, 0.85)"; // 預設顏色：紅色
      });
  }
  useEffect(() => {
    if (hoveredTransactionNode || currentAddress) {
      updateNodeColors();
    }
  }, [hoveredTransactionNode, currentAddress]);
  useEffect(() => {
    if (currentAddress) {
      fetchBalance(currentAddress, selectedChainType); // selectedChainType 仍然作為參數，但不作為觸發條件
    }
  }, [currentAddress]);

  // -----------------------------
  // 5. 更新模擬 & 更新圖表
  // -----------------------------
  function updateSimulation() {
    const nodes = nodesRef.current;
    const links = linksRef.current;
    const simulation = simulationRef.current;

    if (!simulation) return;

    simulation.nodes(nodes);
    simulation.force("link").links(links);
    simulation.alpha(0.3).restart(); // 僅在必要時調整 alpha
    updateGraph();
  }

  function updateGraph() {
    console.log("updateGraph is called");
    const linkGroup = linkGroupRef.current;
    const nodeGroup = nodeGroupRef.current;
    const svg = d3.select(svgRef.current);

    if (!linkGroup || !nodeGroup) {
      console.warn("Link or node group not initialized");
      return;
    }

    const nodes = nodesRef.current;
    const links = linksRef.current;

    console.log("Binding data:", {
      nodes: nodes.length,
      links: links.length,
    });

    // --- LINK ---
    let linkElements = linkGroup
      .selectAll("line")
      .data(links, (link) => {
        const srcId =
          typeof link.source === "object" ? link.source.id : link.source;
        const tgtId =
          typeof link.target === "object" ? link.target.id : link.target;
        return srcId + "-" + tgtId;
      });

    linkElements.exit().remove();

    let linkEnter = linkElements
      .enter()
      .append("line")
      .attr("stroke-width", 2)
      .attr("stroke", "rgb(127, 127, 127)")
      .attr("opacity", "0.6")
      .attr("marker-end", "url(#arrowhead)");

    linkElements = linkEnter.merge(linkElements);

    // --- NODE ---
    let nodeElements = nodeGroup.selectAll("circle").data(nodes, (n) => n.id);

    nodeElements.exit().remove();

    let dragDrop = d3
      .drag()
      .on("start", (event, d) => {
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        simulationRef.current.alphaTarget(0).restart();
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) simulationRef.current.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    let nodeEnter = nodeElements
      .enter()
      .append("circle")
      .attr("r", (node) => {
        let chainType = node.chainType || "BTC";
        let balance =
          chainType === "BTC"
            ? discoveredAddressesRef.current.get(node.id)?.final_balance /
            100000000.0 || 0
            : discoveredAddressesRef.current.get(node.id)?.final_balance || 0;

        // 節點大小與餘額相關
        return Math.min(50, Math.max(5, Math.log(Math.max(1, balance)) * 10 + 10));
      })
      .attr("fill", (node) => {
        // 基於 currentAddress 高亮
        if (node.id === currentAddress) {
          return "rgba(255, 255, 0, 0.85)"; // 高亮顏色：黃色
        }
        // 如果是 hoveredTransactionNode，高亮為綠色
        if (hoveredTransactionNode && node.id === hoveredTransactionNode) {
          return "rgba(0, 200, 0, 0.85)"; // 高亮顏色：綠色
        }
        // 預設為紅色
        return "rgba(255, 0, 0, 0.85)"; // 預設顏色：紅色
      })
      .attr("cx", (node) => node.x || svgRef.current.clientWidth / 2)
      .attr("cy", (node) => node.y || svgRef.current.clientHeight / 2)
      .style("cursor", "pointer")
      .call(dragDrop)
      .on("click", (event, nodeData) => {
        selectNode(nodeData);
      })
      .on("mouseenter", (event, node) => setHoveredNode(node.id)) // 更新高亮節點
      .on("mouseleave", () => setHoveredNode(null)); // 清除高亮節點

    nodeElements = nodeEnter.merge(nodeElements);

    // 監聽 tick
    simulationRef.current.nodes(nodes).on("tick", () => {
      const width = svgRef.current.clientWidth;
      const height = svgRef.current.clientHeight;

      nodeElements
        .attr("cx", (node) => {
          node.x = Math.max(10, Math.min(width - 10, node.x || 0));
          return node.x;
        })
        .attr("cy", (node) => {
          node.y = Math.max(10, Math.min(height - 10, node.y || 0));
          return node.y;
        });

      linkElements
        .attr("x1", (link) => link.source.x || 0)
        .attr("y1", (link) => link.source.y || 0)
        .attr("x2", (link) => link.target.x || 0)
        .attr("y2", (link) => link.target.y || 0);
    });

    simulationRef.current.force("link").links(links);
    simulationRef.current.alphaTarget(0).restart();
    updateNodeColors();

  }

  function selectNode(selectedNode) {
    if (!selectedNode?.id) {
      console.warn("No node id found in selectedNode");
      return;
    }
    if (window.M) {
      window.M.toast({ html: "Loading " + selectedNode.id, displayLength: 2000 });
    }
    lookup(
      selectedNode.id,
      0,
      (result) => {
        updateBlockchain(selectedNode.id, result, 0, selectedNode.distance);
      },
      (status) => {
        if (window.M) {
          window.M.toast({ html: "Error:" + status, displayLength: Infinity });
        }
        console.error("Error", status);
      }
    );
  }
  // -----------------------------
  // 6. 交易/區塊鏈 相關邏輯
  // -----------------------------
  function lookup(input, offset, callback, error) {
    axios
      .get(`${API_URL}/api/lookup`, {
        params: { chainType: selectedChainType, address: input },
        withCredentials: true,
      })
      .then((response) => {
        console.log("Lookup result:", response.data);
        callback(response.data);
      })
      .catch((err) => {
        console.error(err);
        error(err);
      });
  }

  function processTransactions(transactions, chainType) {
    const discoveredAddresses = discoveredAddressesRef.current;
    const linkedAddresses = linkedAddressesRef.current;
    const nodes = nodesRef.current;
    const links = linksRef.current;
    const MAX_NODES = 100;

    const addNode = ({ address, label, amount, group, chainType }) => {
      const value = Math.abs(parseFloat(amount));
      if (!discoveredAddresses.has(address)) {
        discoveredAddresses.set(address, { final_balance: value });
        nodes.push({
          id: address,
          group,
          chainType,
          label: label || "未知",
        });
      } else {
        const oldData = discoveredAddresses.get(address);
        discoveredAddresses.set(address, {
          final_balance: oldData.final_balance + value,
        });
      }
    };

    const addLink = ({ source, target, value, hash, tx }) => {
      if (!linkedAddresses.has(source)) {
        linkedAddresses.set(source, { out: new Map(), in: new Map(), all: new Map() });
      }
      if (!linkedAddresses.has(target)) {
        linkedAddresses.set(target, { out: new Map(), in: new Map(), all: new Map() });
      }

      if (!linkedAddresses.get(source).out.has(hash)) {
        linkedAddresses.get(source).out.set(hash, tx);
      }
      if (!linkedAddresses.get(target).in.has(hash)) {
        linkedAddresses.get(target).in.set(hash, tx);
      }

      linkedAddresses.get(source).all.set(hash, tx);
      linkedAddresses.get(target).all.set(hash, tx);

      links.push({ source, target, value });
    };

    const processBTCTransactions = (tx) => {
      const { hash, txId = hash, inputs, outputs, transactionTime } = tx;
      const timestamp = parseInt(transactionTime, 10);

      if (nodes.length >= MAX_NODES || timestamp < dateMin * 1000 || timestamp > dateMax * 1000) return;

      // 處理每筆輸入
      inputs.forEach((input) => {
        const sourceAddress = input.address;
        const inputValue = parseFloat(input.value);
        const inputLabel = input.label || "未知";

        // 添加輸入節點
        addNode({
          address: sourceAddress,
          label: inputLabel,
          amount: inputValue,
          group: 1,
          chainType,
        });

        // 與當前輸入相關的所有輸出
        outputs.forEach((output) => {
          const targetAddress = output.address;
          const outputValue = parseFloat(output.value);
          const outputLabel = output.label || "未知";

          // 添加輸出節點
          addNode({
            address: targetAddress,
            label: outputLabel,
            amount: outputValue,
            group: 2,
            chainType,
          });

          // 添加連接
          addLink({
            source: sourceAddress,
            target: targetAddress,
            value: outputValue,
            hash: txId,
            tx,
          });

          // 更新交易列表
          setAllTransactions((prev) => [
            ...prev,
            {
              hash: txId,
              source: [
                {
                  address: sourceAddress,
                  amount: inputValue,
                  label: inputLabel,
                },
              ],
              target: [
                {
                  address: targetAddress,
                  amount: outputValue,
                  label: outputLabel,
                },
              ],
              value: outputValue,
              chainType,
              timestamp,
              fromLabel: `${sourceAddress} (${inputLabel})`,
              toLabel: `${targetAddress} (${outputLabel})`,
            },
          ]);
        });
      });
    };

    const processOtherTransactions = (tx) => {
      const { hash, txId = hash, transactionTime, from = [], to = [] } = tx;
    
      // 如果是 TRON，將 transactionTime 轉換為毫秒
      const timestamp = chainType === "TRON" ? parseInt(transactionTime, 10) * 1000 : parseInt(transactionTime, 10);
    
      // 篩選條件
      if (nodes.length >= MAX_NODES || timestamp < dateMin * 1000 || timestamp > dateMax * 1000) return;
    
      const value = Math.abs(parseFloat(tx.amount));
      if (value > 0) {
        setAllTransactions((prev) => [
          ...prev,
          {
            hash: txId,
            source: from.map((f) => ({
              address: f.address,
              amount: f.amount || value,
              label: f.label || "未知",
            })),
            target: to.map((t) => ({
              address: t.address,
              amount: t.amount || value,
              label: t.label || "未知",
            })),
            value,
            chainType,
            timestamp,
            fromLabel: from.map((f) => `${f.address} (${f.label || "未知"})`).join(", "),
            toLabel: to.map((t) => `${t.address} (${t.label || "未知"})`).join(", "),
          },
        ]);
    
        // 添加節點數據
        from.forEach((f) =>
          addNode({
            address: f.address,
            label: f.label || "未知",
            amount: f.amount || value,
            group: chainType === "ETH" || chainType === "BSC" ? 1 : 2,
            chainType,
          })
        );
        to.forEach((t) =>
          addNode({
            address: t.address,
            label: t.label || "未知",
            amount: t.amount || value,
            group: chainType === "ETH" || chainType === "BSC" ? 1 : 2,
            chainType,
          })
        );
    
        // 添加連接數據
        from.forEach((sourceObj) => {
          to.forEach((targetObj) => {
            addLink({
              source: sourceObj.address,
              target: targetObj.address,
              value,
              hash: txId,
              tx,
            });
          });
        });
      }
    };

    transactions.forEach((tx) => {
      if (chainType === "BTC") {
        processBTCTransactions(tx);
      } else {
        processOtherTransactions(tx);
      }
    });

    // 更新 D3 模擬
    updateSimulation();
  }

  function updateBlockchain(address, data, offset, distance) {
    const links = linksRef.current;

    if (data.transactions?.length > 0) {
      console.log(`Processing ${data.transactions.length} ${selectedChainType} transactions`);
      processTransactions(data.transactions, selectedChainType);
      while (links.length > linksMax) links.shift();
      updateSimulation();
    }
  }
  function trace(hash) {
    if (!hash) {
      console.warn("trace() was called but hash is null or undefined.");
      return false;
    }

    console.log("Trace input:", hash);
    if (window.M) {
      window.M.toast({ html: "Loading " + hash, displayLength: 2000 });
    }

    // 重置
    setAllTransactions([]);
    setCurrentAddress(hash);

    nodesRef.current = [];
    linksRef.current = [];
    estimatedAddresesRef.current = new Map();
    discoveredAddressesRef.current = new Map();
    discoveredLinksRef.current = new Set();
    linkedAddressesRef.current = new Map();

    lookup(
      hash,
      0,
      (result) => {
        updateBlockchain(hash, result, 0, 0);
      },
      (status) => {
        console.error("Error", status);
      }
    );
    return false;
  }
  // 追蹤汙染 (若需要)
  function traceTransactionOut(address, hash, index) {
    if (fillStyle < 2) setFillStyle(2);
    updateSimulation();
  }
  function traceTransactionIn(address, hash, index) {
    if (fillStyle < 2) setFillStyle(2);
    updateSimulation();
  }

  // -----------------------------
  // 7. 回傳給外部使用
  // -----------------------------
  return {
    // D3 用到的參考
    svgRef,
    linkGroupRef,
    nodeGroupRef,
    // 主要狀態
    selectedChainType,
    setSelectedChainType,
    fillStyle,
    setFillStyle,
    dateMin,
    setDateMin,
    dateMax,
    setDateMax,
    currentAddress,
    allTransactions,
    setHoveredTransactionNode,
    balanceData,
    isFetchingBalance,
    fetchBalance,
    // 主要方法
    trace,
    traceTransactionOut,
    traceTransactionIn,
  };
}
